/* istanbul ignore file */

import type { ComponentProps } from 'react';
import React, { memo, useCallback } from 'react';
import type { LayoutChangeEvent, ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import type { SharedValue, useAnimatedStyle } from 'react-native-reanimated';
import Animated, { withTiming } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';
import { theme, useResponsive } from '@sg/garnish';

import {
  useLocationResultsExpandCollapse,
  useLocationResultsScrolling,
} from './hooks';
import { useLocationResultsExpandCollapseStyles } from './hooks/useLocationResultsExpandCollapseStyle';
import { LOCATION_RESULTS_BORDER_RADIUS } from './LocationResults.constants';
import {
  LocationResultsBackdrop,
  LocationResultsBottomShadow,
  LocationResultsLists,
  LocationResultsMoreLocationsToggle,
} from './subcomponents';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders location lists in a dynamic container that can be expanded and collapsed.
 * It employs a two-container (outer and inner) approach to correctly calculate
 * collapsed and expanded layouts, as well as smooth animations utilizing the
 * `translateY` animated style to avoid repainting.
 *
 * It supports the following modes/states:
 *
 * - Loading state
 * - A single location view
 * - Multiple locations
 * - Multiple locations + other locations
 */
export const LocationResults = memo((props: LocationResultsProps) => {
  const {
    locations = [],
    otherLocations = [],
    locationSearchType,
    activePinLocationId,
    interactedLocationRestaurantSlug,
    isLoading,
    shouldHideDetailsModal,
    withSafeAreaInsets = false,
    onCardPress,
    onCardFocus,
    onLocationResultsExpandCollapse,
  } = props;

  const { currentBreakpoint } = useResponsive();

  const totalLocationsNumber = locations.length + otherLocations.length;
  const hasNoResults = !isLoading && totalLocationsNumber === 0;
  const hasSingleResult = totalLocationsNumber === 1;
  const hasSingleOtherResult = hasSingleResult && otherLocations.length === 1;
  const hasMultipleResults = totalLocationsNumber > 1;
  const shouldUseDetailsModal =
    locationSearchType !== 'delivery' && !shouldHideDetailsModal;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const { isExpanded, toggleExpandedState } = useLocationResultsExpandCollapse({
    locationSearchType,
    hasSingleResult,
    onLocationResultsExpandCollapse,
  });

  const {
    outerContainerHeight,
    innerContainerAnimatedStyle,
    innerContainerCollapsedStyle,
  } = useLocationResultsExpandCollapseStyles({
    isExpanded,
    hasMultipleResults,
    hasSingleOtherResult,
  });

  const {
    listScrollViewRef,
    setListItemScrollPosition,
    deleteListItemScrollPosition,
  } = useLocationResultsScrolling({
    activePinLocationId,
    isExpanded,
    locationSearchType,
  });

  // ─────────────────────────────────────────────────────────────────────

  if (hasNoResults) return null;

  if (!currentBreakpoint.isXS) {
    return (
      <LocationResultsLists
        ref={listScrollViewRef}
        locationSearchType={locationSearchType}
        locations={locations}
        otherLocations={otherLocations}
        interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
        isLoading={isLoading}
        withDetailsModal={shouldUseDetailsModal}
        containerHeightSharedValue={outerContainerHeight}
        hasSingleResult={hasSingleResult}
        isExpanded
        onCardLayout={setListItemScrollPosition}
        onCardPress={onCardPress}
        onCardFocus={onCardFocus}
        onCardUnmount={deleteListItemScrollPosition}
      />
    );
  }

  return (
    <>
      <LocationResultsBackdrop isVisible={isExpanded} />
      <LocationResultsBottomShadow
        innerContainerCollapsedStyle={innerContainerCollapsedStyle}
        innerContainerAnimatedStyle={innerContainerAnimatedStyle}
      />

      <LocationResultsOuterContainer
        outerContainerHeight={outerContainerHeight}
        withSafeAreaInsets={withSafeAreaInsets}
      >
        <LocationResultsInnerContainer
          innerContainerAnimatedStyle={innerContainerAnimatedStyle}
        >
          {hasMultipleResults ? (
            <LocationResultsMoreLocationsToggle
              isToggled={isExpanded}
              onPress={toggleExpandedState}
            />
          ) : null}

          <LocationResultsLists
            ref={listScrollViewRef}
            locationSearchType={locationSearchType}
            locations={locations}
            otherLocations={otherLocations}
            interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
            isLoading={isLoading}
            withDetailsModal={shouldUseDetailsModal}
            containerHeightSharedValue={outerContainerHeight}
            hasSingleResult={hasSingleResult}
            isExpanded={isExpanded}
            onCardLayout={setListItemScrollPosition}
            onCardPress={onCardPress}
            onCardFocus={onCardFocus}
            onCardUnmount={deleteListItemScrollPosition}
          />
        </LocationResultsInnerContainer>
      </LocationResultsOuterContainer>
    </>
  );
});

// ─── Subcomponents ───────────────────────────────────────────────────────────

/**
 * Renders a `View` container component that fills available space and synchronizes
 * its height with the associated shared value for subsequent calculations.
 */
const LocationResultsOuterContainer = memo(
  (props: LocationResultsOuterContainerProps) => {
    const { children, outerContainerHeight, withSafeAreaInsets } = props;

    const { bottom: safeAreaBottom } = useSafeAreaInsets();
    const shouldUseSafeAreaInsets = safeAreaBottom > 0 && withSafeAreaInsets;

    const setContainerHeightValue = useCallback(
      (event: LayoutChangeEvent) => {
        const { layout } = event.nativeEvent;

        // eslint-disable-next-line functional/immutable-data
        outerContainerHeight.value = withTiming(layout.height);
      },
      [outerContainerHeight],
    );

    const outerContainerDynamicStyles = useStyle(
      () => ({
        marginBottom: shouldUseSafeAreaInsets
          ? safeAreaBottom
          : theme.spacing['4'],
      }),
      [safeAreaBottom],
    );

    return (
      <View
        style={[styles.outerContainer, outerContainerDynamicStyles]}
        onLayout={setContainerHeightValue}
        pointerEvents="box-none"
      >
        {children}
      </View>
    );
  },
);

/**
 * Renders an animated `View` component that can be expanded/collapsed externally using
 * animated styles to use smooth animations and prevent repainting.
 */
const LocationResultsInnerContainer = memo(
  (props: LocationResultsInnerContainerProps) => {
    const { children, innerContainerAnimatedStyle } = props;

    const innerContainerStyles = [
      styles.innerContainer,
      innerContainerAnimatedStyle,
    ];

    return (
      <Animated.View style={innerContainerStyles} pointerEvents="box-none">
        {children}
      </Animated.View>
    );
  },
);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    ...StyleSheet.absoluteFillObject,
    zIndex: theme.zIndex.fixed,
    borderRadius: LOCATION_RESULTS_BORDER_RADIUS,
    marginTop: theme.spacing['4'],
    marginHorizontal: theme.spacing['4'],
    overflow: 'hidden',
  },
  innerContainer: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultsProps = Readonly<{
  activePinLocationId: string | undefined;
  shouldHideDetailsModal: boolean;
  onLocationResultsExpandCollapse?: (isExpanded: boolean) => void;
  withSafeAreaInsets?: boolean;
}> &
  Pick<
    ComponentProps<typeof LocationResultsLists>,
    | 'locations'
    | 'otherLocations'
    | 'locationSearchType'
    | 'interactedLocationRestaurantSlug'
    | 'isLoading'
    | 'onCardPress'
    | 'onCardFocus'
  >;

type LocationResultsOuterContainerProps = Readonly<{
  children: ViewProps['children'];
  outerContainerHeight: SharedValue<number>;
  withSafeAreaInsets: boolean;
}>;

type LocationResultsInnerContainerProps = Readonly<{
  children: ViewProps['children'];
  innerContainerAnimatedStyle: ReturnType<typeof useAnimatedStyle>;
}>;
